import React, { useState } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Paper, TextField, Typography, CircularProgress, Grid,
    Divider, FormControl, InputLabel, Select, MenuItem, Collapse, IconButton,
    Chip, Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useFirebase } from '../../context/FirebaseContext';
import { getServicesStatusV2, testAnalysisServiceV2, executeAdminToolV2 } from '../../api/admin';
import logger from '../../utils/logger';
import './AdminView.css';

const TOOL_DEFAULTS = {
    getMarketData: '{\n  "instrument": "EUR_USD",\n  "timeframe": "H1",\n  "count": 50\n}',
    getNewsEvents: '{\n  "instrument": "EUR_USD"\n}',
    getTechnicalIndicators: '{\n  "instrument": "EUR_USD",\n  "timeframe": "H1"\n}',
    getKeyLevels: '{\n  "instrument": "EUR_USD",\n  "timeframe": "H4"\n}',
    execute_python_code: '{\n  "code": "import math\\nprint(f\'Pi is approximately {math.pi:.4f}\')"\n}'
};

const ServicesTab = () => {
    const { auth } = useFirebase();
    const [loading, setLoading] = useState(false);
    const [servicesStatus, setServicesStatus] = useState(null);
    const [testResult, setTestResult] = useState(null);

    // Tool Tester State
    const [selectedTool, setSelectedTool] = useState('getMarketData');
    const [toolArgs, setToolArgs] = useState(TOOL_DEFAULTS['getMarketData']);
    const [executingTool, setExecutingTool] = useState(false);
    const [toolResult, setToolResult] = useState(null);

    // Help Section State
    const [helpExpanded, setHelpExpanded] = useState(false);
    const [copiedEndpoint, setCopiedEndpoint] = useState(null);

    const copyToClipboard = (text, endpoint) => {
        navigator.clipboard.writeText(text);
        setCopiedEndpoint(endpoint);
        setTimeout(() => setCopiedEndpoint(null), 2000);
    };

    const handleRefreshStatus = async () => {
        setLoading(true);
        try {
            const response = await new Promise((resolve) => {
                getServicesStatusV2(auth, resolve);
            });
            setServicesStatus(response);
        } catch (error) {
            logger.error('Error getting services status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTestService = async () => {
        setLoading(true);
        setTestResult(null);
        try {
            const response = await new Promise((resolve) => {
                testAnalysisServiceV2(auth, resolve);
            });
            setTestResult(response);
        } catch (error) {
            logger.error('Error testing service:', error);
            setTestResult({ success: false, error: error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteTool = async () => {
        setExecutingTool(true);
        setToolResult(null);
        try {
            let args = {};
            try {
                args = JSON.parse(toolArgs);
            } catch (e) {
                setToolResult({ error: "Invalid JSON arguments" });
                setExecutingTool(false);
                return;
            }

            const response = await new Promise((resolve) => {
                executeAdminToolV2(auth, resolve, selectedTool, args);
            });
            setToolResult(response);
        } catch (error) {
            setToolResult({ error: error.message || "Execution failed" });
        } finally {
            setExecutingTool(false);
        }
    };

    return (
        <Box className="admin-tab-content">
            {/* Services Status Section */}
            <Paper className="admin-section">
                <Box className="admin-section-header">
                    <Typography variant="h6">External Services Status</Typography>
                    <Button
                        variant="outlined"
                        onClick={handleRefreshStatus}
                        disabled={loading}
                        size="small"
                    >
                        Refresh
                    </Button>
                </Box>

                {servicesStatus ? (
                    <>
                        {/* Desktop Table */}
                        <Box className="admin-table-desktop">
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Service</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Version</TableCell>
                                            <TableCell>Latency</TableCell>
                                            <TableCell className="admin-hide-tablet">URL</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {servicesStatus.analysisService && (
                                            <TableRow>
                                                <TableCell>Analysis Service</TableCell>
                                                <TableCell>
                                                    <span className={`admin-status-badge ${servicesStatus.analysisService.available ? 'admin-status-success' : 'admin-status-error'}`}>
                                                        {servicesStatus.analysisService.status}
                                                    </span>
                                                </TableCell>
                                                <TableCell>{servicesStatus.analysisService.version}</TableCell>
                                                <TableCell>{servicesStatus.analysisService.latencyMs}ms</TableCell>
                                                <TableCell className="admin-hide-tablet admin-url-cell">
                                                    {servicesStatus.analysisService.url}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        {/* Mobile Cards */}
                        <Box className="admin-cards-mobile">
                            {servicesStatus.analysisService && (
                                <Paper className="admin-service-card">
                                    <Box className="admin-service-card-header">
                                        <span className="admin-service-name">Analysis Service</span>
                                        <span className={`admin-status-badge ${servicesStatus.analysisService.available ? 'admin-status-success' : 'admin-status-error'}`}>
                                            {servicesStatus.analysisService.status}
                                        </span>
                                    </Box>
                                    <Box className="admin-service-card-body">
                                        <span>Version: {servicesStatus.analysisService.version}</span>
                                        <span>Latency: {servicesStatus.analysisService.latencyMs}ms</span>
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    </>
                ) : (
                    <Box className="admin-empty-prompt">
                        <Typography color="text.secondary">
                            Click "Refresh" to check service availability
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Verify Execution Section */}
            <Paper className="admin-section">
                <Box className="admin-section-header">
                    <Typography variant="h6">Verify Execution</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" className="admin-section-desc">
                    Test the connection by sending a Python calculation request.
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleTestService}
                    disabled={loading}
                    className="admin-test-btn"
                >
                    {loading ? <CircularProgress size={20} /> : 'Run Verification'}
                </Button>

                {testResult && (
                    <Box className="admin-test-result">
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Status</Typography>
                                <Typography className={testResult.success ? 'admin-text-success' : 'admin-text-error'}>
                                    {testResult.success ? 'SUCCESS' : 'FAILED'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Latency</Typography>
                                <Typography>{testResult.latencyMs}ms</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">Code</Typography>
                                <Paper className="admin-code-block">
                                    <pre>{testResult.code}</pre>
                                </Paper>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">Result</Typography>
                                <Paper className="admin-code-block admin-code-result">
                                    <pre>{testResult.result}</pre>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Paper>

            <Divider className="admin-divider" />

            {/* Tool Tester Section */}
            <Paper className="admin-section">
                <Box className="admin-section-header">
                    <Typography variant="h6">Generic Tool Tester</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" className="admin-section-desc">
                    Test arbitrary tool execution.
                </Typography>

                <Grid container spacing={2} className="admin-tool-form">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tool Name</InputLabel>
                            <Select
                                value={selectedTool}
                                label="Tool Name"
                                onChange={(e) => {
                                    const newTool = e.target.value;
                                    setSelectedTool(newTool);
                                    setToolArgs(TOOL_DEFAULTS[newTool] || '{}');
                                }}
                            >
                                <MenuItem value="getMarketData">getMarketData</MenuItem>
                                <MenuItem value="getNewsEvents">getNewsEvents (Web Search)</MenuItem>
                                <MenuItem value="getTechnicalIndicators">getTechnicalIndicators</MenuItem>
                                <MenuItem value="getKeyLevels">getKeyLevels</MenuItem>
                                <MenuItem value="execute_python_code">execute_python_code</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Arguments (JSON)"
                            value={toolArgs}
                            onChange={(e) => setToolArgs(e.target.value)}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            onClick={handleExecuteTool}
                            disabled={executingTool}
                        >
                            {executingTool ? 'Executing...' : 'Run Tool'}
                        </Button>
                    </Grid>
                    {toolResult && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Result:</Typography>
                            <Paper className="admin-code-block admin-result-block">
                                <pre>{JSON.stringify(toolResult, null, 2)}</pre>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <Divider className="admin-divider" />

            {/* Analysis Service API Documentation */}
            <Paper className="admin-section">
                <Box
                    className="admin-section-header"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setHelpExpanded(!helpExpanded)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HelpOutlineIcon color="primary" />
                        <Typography variant="h6">Analysis Service API Documentation</Typography>
                    </Box>
                    <IconButton size="small">
                        {helpExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={helpExpanded}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        {/* Overview */}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            The Analysis Service is a Python FastAPI microservice that executes Python code for
                            mathematical calculations, data analysis, and AI-powered trade validation. It runs
                            in an isolated environment with access to common data science libraries.
                        </Typography>

                        {/* Environment URLs */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Environment URLs
                        </Typography>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Use the correct URL based on your environment:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="DEV" size="small" color="warning" />
                                    <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4 }}>
                                        http://localhost:8090
                                    </code>
                                    <Typography variant="caption" color="text.secondary">
                                        (local Docker or Python)
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="PROD" size="small" color="success" />
                                    <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4 }}>
                                        http://analysis-service:8000
                                    </code>
                                    <Typography variant="caption" color="text.secondary">
                                        (Kubernetes internal service)
                                    </Typography>
                                </Box>
                            </Box>
                        </Alert>

                        {/* API Endpoints */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            API Endpoints
                        </Typography>

                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Endpoint</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell><code>/health</code></TableCell>
                                        <TableCell><Chip label="GET" size="small" color="primary" /></TableCell>
                                        <TableCell>Health check - returns status and version</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><code>/execute</code></TableCell>
                                        <TableCell><Chip label="POST" size="small" color="success" /></TableCell>
                                        <TableCell>Execute Python code with optional timeout</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><code>/search</code></TableCell>
                                        <TableCell><Chip label="POST" size="small" color="success" /></TableCell>
                                        <TableCell>Web search via DuckDuckGo</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell><code>/metrics</code></TableCell>
                                        <TableCell><Chip label="GET" size="small" color="primary" /></TableCell>
                                        <TableCell>Prometheus metrics for monitoring</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Execute Endpoint Details */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            /execute Endpoint
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Executes Python code in a sandboxed environment. Default timeout is 30 seconds.
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Request Body</Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard('{\n  "code": "import math\\nresult = math.sqrt(144)\\nprint(f\'Square root of 144 is {result}\')\\n",\n  "timeout": 30\n}', 'execute-request')}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                    {copiedEndpoint === 'execute-request' && (
                                        <Typography variant="caption" sx={{ ml: 0.5 }}>Copied!</Typography>
                                    )}
                                </IconButton>
                            </Box>
                            <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                                <pre>{`{
  "code": "import math\\nresult = math.sqrt(144)\\nprint(f'Square root of 144 is {result}')\\n",
  "timeout": 30
}`}</pre>
                            </Paper>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" color="text.secondary">Response</Typography>
                            <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                                <pre>{`{
  "success": true,
  "output": "Square root of 144 is 12.0",
  "error": null,
  "execution_time_ms": 45
}`}</pre>
                            </Paper>
                        </Box>

                        {/* Search Endpoint Details */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            /search Endpoint
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Performs web search using DuckDuckGo. Useful for fetching current market news or information.
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Request Body</Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard('{\n  "query": "EUR/USD forex news today",\n  "max_results": 5\n}', 'search-request')}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                    {copiedEndpoint === 'search-request' && (
                                        <Typography variant="caption" sx={{ ml: 0.5 }}>Copied!</Typography>
                                    )}
                                </IconButton>
                            </Box>
                            <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                                <pre>{`{
  "query": "EUR/USD forex news today",
  "max_results": 5
}`}</pre>
                            </Paper>
                        </Box>

                        {/* Available Libraries */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Available Python Libraries
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                            {['pandas', 'numpy', 'scikit-learn', 'yfinance', 'math', 'statistics', 'datetime', 'json', 're'].map(lib => (
                                <Chip key={lib} label={lib} size="small" variant="outlined" />
                            ))}
                        </Box>

                        {/* cURL Examples */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            cURL Examples
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Health Check (Dev)</Typography>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Chip label="DEV" size="small" color="warning" />
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard('curl http://localhost:8090/health', 'curl-health-dev')}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                    {copiedEndpoint === 'curl-health-dev' && (
                                        <Typography variant="caption" sx={{ ml: 0.5 }}>Copied!</Typography>
                                    )}
                                </IconButton>
                            </Box>
                            <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                                <pre>curl http://localhost:8090/health</pre>
                            </Paper>
                        </Box>

                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Execute Python Code (Dev)</Typography>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Chip label="DEV" size="small" color="warning" />
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(`curl -X POST http://localhost:8090/execute \\
  -H "Content-Type: application/json" \\
  -d '{"code": "print(2 + 2)", "timeout": 30}'`, 'curl-execute-dev')}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                    {copiedEndpoint === 'curl-execute-dev' && (
                                        <Typography variant="caption" sx={{ ml: 0.5 }}>Copied!</Typography>
                                    )}
                                </IconButton>
                            </Box>
                            <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                                <pre>{`curl -X POST http://localhost:8090/execute \\
  -H "Content-Type: application/json" \\
  -d '{"code": "print(2 + 2)", "timeout": 30}'`}</pre>
                            </Paper>
                        </Box>

                        {/* Local Development Setup */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Local Development Setup
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Option 1: Docker (Recommended)</Typography>
                        <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4', mb: 2 }}>
                            <pre>{`cd analysis_service
./run_docker.sh
# Or manually:
docker build -t analysis-service .
docker run -p 8090:8000 analysis-service`}</pre>
                        </Paper>

                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Option 2: Python Virtual Environment</Typography>
                        <Paper className="admin-code-block" sx={{ backgroundColor: '#1e1e1e', color: '#d4d4d4', mb: 2 }}>
                            <pre>{`cd analysis_service
python -m venv venv
source venv/bin/activate  # Windows: venv\\Scripts\\activate
pip install -r requirements.txt
uvicorn main:app --host 0.0.0.0 --port 8090 --reload`}</pre>
                        </Paper>

                        {/* Troubleshooting */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Troubleshooting
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Issue</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Solution</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Connection refused on localhost:8090</TableCell>
                                        <TableCell>Ensure the service is running via Docker or uvicorn</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Timeout errors</TableCell>
                                        <TableCell>Increase timeout in request (max 60s) or optimize code</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Module not found errors</TableCell>
                                        <TableCell>Only standard library + listed packages are available</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Service unavailable in prod</TableCell>
                                        <TableCell>Check K8s pod status: <code>kubectl get pods -l app=analysis-service</code></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
};

export default ServicesTab;
